import {
    Account,
    ApiToken,
    AuthenticatorCodeGeneration,
    CardDetailsRequest,
    CardDetailsResponse,
    ChangePasswordRequest,
    ContactDetails,
    ContactDetailsRequest,
    CreateAndPayForServerRequest,
    CreateAndPayForServerResult,
    CreateServerRequest,
    DomainAction,
    DomainOverview,
    DomainRegistrationPlan,
    DomainRenewalPlan,
    DomainRenewalRequest,
    DomainRenewalResultResponse,
    DomainTransferPlan,
    DomainTransferRequest,
    DomainTransferResultResponse,
    DomainsRegistrationRequest,
    DomainsRegistrationResultResponse,
    EmailSubscriptions,
    GetSiteServicesV2AuthenticatorGenerateNewSecretResult,
    GetSiteServicesV2BillingDetailsCardDetailsResult,
    GetSiteServicesV2PanelMigrationResult,
    GetSiteServicesV2ServersServerIdValidRegionsResult,
    GlueRecordsRequest,
    InvoicePaymentResult,
    LoginRequest,
    LoginResult,
    NameServersRequest,
    PanelMigrationStatus,
    PaypalCancellationRequest,
    PaypalCompletionResult,
    PaypalSuccessRequest,
    PaypalDepositResult,
    PhoneVerificationSetupResult,
    PostSiteServicesV2AddCreditPaypalResult,
    PutSiteServicesV2DomainManagementDomainNameUpdateContactDetailsResult,
    PostSiteServicesV2DomainRegistrationDomainNameGetTransferPlanResult,
    PostSiteServicesV2DomainRegistrationGetRegistrationPlansResult,
    PostSiteServicesV2InvoicePaymentRetryFailedResult,
    PostSiteServicesV2SignupValidateServerResult,
    PostSiteServicesV2UploadBackupResult,
    PurchaseComplete,
    PutSiteServicesV2PanelMigrationResult,
    PutSiteServicesV2SubscriptionsResult,
    RegisterAccount,
    RegisterAccountRequest,
    RegisteredDomain,
    RegionsResponseUnpaged,
    RetryInvoicePaymentsResult,
    SaveCardDetails,
    SendEmailCodeRequest,
    SendLoginCodeViaSmsRequest,
    VerificationSendResult,
    SetAuthenticatorSecretRequest,
    SetupPaypalResult,
    PasswordResetTokenResult,
    ResetPasswordUsingTokenRequest,
    VerifyPasswordResetTokenRequest,
    SetupPhoneVerificationRequest,
    SignupRequest,
    SignupResult,
    SshKey,
    UpdatePanelMigrationStatusRequest,
    UploadLocalImageRequest,
} from './ApiModel';
import { PagedItem, PageSpec } from './helper/PagingHelper';
import * as OpenApi from './OpenApiClient';

import { ApiFetch, failedResponseAsProblemDetails } from './helper/RequestHelper';
import WebApiClient from './WebApiClient';

export interface PasswordInfo {
    username: string;
    password: string | null;
    ssh_keys: SshKey[];
}

export interface ISiteServicesApiClient {
    updatePanelMigrationStatus(status: UpdatePanelMigrationStatusRequest): Promise<PanelMigrationStatus>;
    getPanelMigrationStatus(): Promise<PanelMigrationStatus>;
    getEmailSubscriptions(): Promise<EmailSubscriptions>;
    updateEmailSubscriptions(subscriptions: EmailSubscriptions): Promise<EmailSubscriptions>;
    getContactDetails(): Promise<ContactDetails>;
    updateContactDetails(request: ContactDetailsRequest): Promise<ContactDetails>;
    getSupportCode(cancellationToken?: AbortSignal): Promise<string>;
    regenerateSupportCode(): Promise<string>;
    getApiTokens(cancellationToken?: AbortSignal): Promise<ApiToken[]>;
    createApiToken(name: string): Promise<ApiToken>;
    updateApiToken(id: string, name: string): Promise<ApiToken>;
    regenerateApiToken(id: string): Promise<ApiToken>;
    deleteApiToken(id: string): Promise<void>;
    fetchPasswordInfo(passwordActionId: number): Promise<PasswordInfo>;
    uploadBackup(request: UploadLocalImageRequest): Promise<PostSiteServicesV2UploadBackupResult>;
    uploadBackupChunk(actionId: number, chunk: Blob): Promise<void>;
    getValidRegions(serverId: number, cancellationToken?: AbortSignal): Promise<RegionsResponseUnpaged>;
    login(loginRequest: LoginRequest): Promise<LoginResult>;
    sendEmailCode(request: SendEmailCodeRequest): Promise<void>;
    sendCodeViaSms(request: SendLoginCodeViaSmsRequest): Promise<VerificationSendResult>;
    generateAuthenticatorSecret(): Promise<AuthenticatorCodeGeneration>;
    setAuthenticatorSecret(request: SetAuthenticatorSecretRequest): Promise<Account>;
    disableAuthenticator(): Promise<Account>;
    keepAlive(): Promise<void>;
    payInvoiceWithStoreCredit(invoiceNumber: number): Promise<InvoicePaymentResult>;
    payInvoiceWithPaypal(invoiceNumber: number, completeUrl: string, actionId?: number): Promise<PaypalDepositResult>;
    payDepositWithPaypal(amount: number, description: string, completeUrl: string): Promise<PaypalDepositResult>;
    setupPaypal(successUrl: string): Promise<SetupPaypalResult>;
    retryFailedInvoicePayments(): Promise<RetryInvoicePaymentsResult>;
    completePaypalPayment(success: PaypalSuccessRequest): Promise<PaypalCompletionResult>;
    cancelPaypalPayment(cancellation: PaypalCancellationRequest): Promise<PaypalCompletionResult>;
    getRegisteredDomain(domainName: string): Promise<RegisteredDomain>;
    getManagedDomains(pageSpec?: PageSpec, cancellationToken?: AbortSignal): Promise<PagedItem<DomainOverview>>;
    updateDomainNameservers(domainName: string, request: NameServersRequest): Promise<RegisteredDomain>;
    updateGlueRecords(domainName: string, request: GlueRecordsRequest): Promise<RegisteredDomain>;
    updateDomainRegistrantDetails(domainName: string, request: ContactDetails): Promise<RegisteredDomain>;
    enableDomainPrivacy(domainName: string): Promise<RegisteredDomain>;
    disableDomainPrivacy(domainName: string): Promise<RegisteredDomain>;
    lockDomain(domainName: string): Promise<RegisteredDomain>;
    unlockDomain(domainName: string): Promise<RegisteredDomain>;
    disableRenewalReminders(domainName: string): Promise<RegisteredDomain>;
    enableRenewalReminders(domainName: string): Promise<RegisteredDomain>;
    generateAuthCode(domainName: string): Promise<RegisteredDomain>;
    getDomainRenewalPlan(domainName: string): Promise<DomainRenewalPlan>;
    renewDomain(domainName: string, request: DomainRenewalRequest): Promise<DomainRenewalResultResponse>;
    getDomainAction(actionId: number, cancellationToken?: AbortSignal): Promise<DomainAction>;
    getEnabledRegistrationPlans(): Promise<DomainRegistrationPlan[]>;
    getDomainTransferPlan(domainName: string, authCode: string): Promise<DomainTransferPlan>;
    transferDomain(domainName: string, request: DomainTransferRequest): Promise<DomainTransferResultResponse>;
    getDomainRegistrationPlans(domain: string): Promise<DomainRegistrationPlan[]>;
    registerDomains(request: DomainsRegistrationRequest): Promise<DomainsRegistrationResultResponse>;
    sendResetToken(email: string): Promise<void>;
    resetPasswordUsingToken(request: ResetPasswordUsingTokenRequest): Promise<PasswordResetTokenResult>;
    checkPasswordResetToken(request: VerifyPasswordResetTokenRequest): Promise<PasswordResetTokenResult>;
    logout(): Promise<void>;
    getCardDetails(): Promise<CardDetailsResponse>;
    updateCardDetails(request: CardDetailsRequest): Promise<SaveCardDetails>;
    deleteCardDetails(): Promise<void>;
    changePassword(request: ChangePasswordRequest): Promise<void>;
    register(request: RegisterAccountRequest): Promise<RegisterAccount>;
    validateServerCreateParams(request: CreateServerRequest): Promise<PostSiteServicesV2SignupValidateServerResult>;
    setupPhoneVerification(request: SetupPhoneVerificationRequest): Promise<PhoneVerificationSetupResult>;
    signup(request: SignupRequest): Promise<SignupResult>;
    emailConfirmationRequired(cancellationToken?: AbortSignal): Promise<boolean>;
    sendEmailConfirmation(): Promise<boolean>;
    confirmEmail(id: string): Promise<void>;
    createServer(request: CreateAndPayForServerRequest): Promise<CreateAndPayForServerResult>;
    getPurchaseComplete(serverId: number, cancellationToken?: AbortSignal): Promise<PurchaseComplete>;
    getZendeskMessagingJwt(): Promise<string>;
    getFreshdeskSso(redirectTo?: string, hostUrl?: string, cancellationToken?: AbortSignal): Promise<string>;
    getZendeskSso(returnTo?: string, cancellationToken?: AbortSignal): Promise<string>;
    fetchInvoiceHtml(invoiceId: number): Promise<string>;
    downloadInvoicePdf(invoiceId: number, invoiceNumber: string): Promise<void>;
}

/** Bind the first `client` parameter of an OpenApiClient function to this instance. */
function delegate<A extends unknown[], R>(
    target: WebApiClient,
    fn: (client: WebApiClient, ...args: A) => R
): (...args: A) => R {
    return (...args: A) => fn(target, ...args);
}

/** Shared so a page needing its own client (PortalRedirect) cannot drift from the wired one. */
export const SiteServicesEndpoint = '/api/site-services/v2';

export class SiteServicesApiClient extends WebApiClient implements ISiteServicesApiClient {
    // Delegated to OpenApiClient
    getEmailSubscriptions = delegate(this, OpenApi.getSubscriptions);
    getContactDetails = delegate(this, OpenApi.getAccountManagementContactDetails);
    updateContactDetails = delegate(this, OpenApi.postAccountManagementContactDetails);
    getSupportCode = delegate(this, OpenApi.getAccountManagementSupportCode);
    regenerateSupportCode = delegate(this, OpenApi.postAccountManagementSupportCodeRegenerate);
    getApiTokens = delegate(this, OpenApi.getApiTokens);
    regenerateApiToken = delegate(this, OpenApi.postApiTokenRegenerate);
    deleteApiToken = delegate(this, OpenApi.deleteApiToken);
    setAuthenticatorSecret = delegate(this, OpenApi.postAuthenticatorSetNewSecret);
    disableAuthenticator = delegate(this, OpenApi.postAuthenticatorDisableAuthenticator);
    keepAlive = delegate(this, OpenApi.getAuthKeepAlive);
    login = delegate(this, OpenApi.postAuthLogin);
    sendEmailCode = delegate(this, OpenApi.postAuthSendEmailCode);
    sendCodeViaSms = delegate(this, OpenApi.postAuthSendCodeViaSms);
    logout = delegate(this, OpenApi.postAuthLogout);
    changePassword = delegate(this, OpenApi.postAccountManagementChangePassword);
    payInvoiceWithStoreCredit = delegate(this, OpenApi.postInvoicePaymentPayWithStoreCredit);
    completePaypalPayment = delegate(this, OpenApi.postPaypalCompletionSuccess);
    cancelPaypalPayment = delegate(this, OpenApi.postPaypalCompletionCancel);
    updateCardDetails = delegate(this, OpenApi.postBillingDetailsCardDetails);
    deleteCardDetails = delegate(this, OpenApi.deleteBillingDetailsCardDetails);
    getRegisteredDomain = delegate(this, OpenApi.getDomainManagement);
    getManagedDomains = delegate(this, OpenApi.getDomainManagements);
    updateDomainNameservers = delegate(this, OpenApi.putDomainManagementUpdateNameServers);
    updateGlueRecords = delegate(this, OpenApi.putDomainManagementUpdateHostRegistry);
    enableDomainPrivacy = delegate(this, OpenApi.putDomainManagementEnablePrivacyProtection);
    disableDomainPrivacy = delegate(this, OpenApi.putDomainManagementDisablePrivacyProtection);
    lockDomain = delegate(this, OpenApi.putDomainManagementLock);
    unlockDomain = delegate(this, OpenApi.putDomainManagementUnlock);
    disableRenewalReminders = delegate(this, OpenApi.putDomainManagementDisableRenewalReminders);
    enableRenewalReminders = delegate(this, OpenApi.putDomainManagementEnableRenewalReminders);
    generateAuthCode = delegate(this, OpenApi.postDomainManagementAuthCode);
    getDomainRenewalPlan = delegate(this, OpenApi.getDomainRegistrationGetRenewalPlan);
    renewDomain = delegate(this, OpenApi.postDomainRegistrationRenewDomain);
    getDomainAction = delegate(this, OpenApi.getDomainRegistrationAction);
    getEnabledRegistrationPlans = delegate(this, OpenApi.getDomainRegistrationGetEnabledRegistrationPlans);
    transferDomain = delegate(this, OpenApi.postDomainRegistrationTransferDomain);
    registerDomains = delegate(this, OpenApi.postDomainRegistrationRegisterDomains);
    register = delegate(this, OpenApi.postRegister);
    setupPhoneVerification = delegate(this, OpenApi.postSignupSetupPhoneVerification);
    signup = delegate(this, OpenApi.postSignup);
    emailConfirmationRequired = delegate(this, OpenApi.getSignupEmailConfirmationRequired);
    sendEmailConfirmation = delegate(this, OpenApi.postSignupSendEmailConfirmation);
    confirmEmail = delegate(this, OpenApi.postRegisterConfirmEmail);
    createServer = delegate(this, OpenApi.postCreateAndPayServers);
    getPurchaseComplete = delegate(this, OpenApi.getPurchaseComplete);
    getZendeskMessagingJwt = delegate(this, OpenApi.getZendeskMessagingJwt);
    getFreshdeskSso = delegate(this, OpenApi.getFreshdeskSso);
    getZendeskSso = delegate(this, OpenApi.getZendeskSso);
    sendResetToken = (email: string) => OpenApi.postPasswordResetSendResetToken(this, { email });
    resetPasswordUsingToken = delegate(this, OpenApi.postPasswordReset);
    checkPasswordResetToken = delegate(this, OpenApi.postPasswordResetVerifyPasswordResetToken);

    // Hand-written methods (non-JSON responses)

    async downloadInvoicePdf(invoiceId: number, invoiceNumber: string): Promise<void> {
        const response = await ApiFetch(this.endpoint + `/invoices/${invoiceId}/download`, 'GET', {
            headers: { 'X-CSRF-TOKEN': this.csrfToken },
        });

        if (!response.ok) {
            const err = await failedResponseAsProblemDetails(response);
            this.handlePossibleAuthError(err);
        }

        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Invoice-${invoiceNumber}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
    }

    async fetchInvoiceHtml(invoiceId: number): Promise<string> {
        const response = await ApiFetch(this.endpoint + `/invoices/${invoiceId}/render`, 'GET', {
            headers: { 'X-CSRF-TOKEN': this.csrfToken },
        });

        if (!response.ok) {
            const err = await failedResponseAsProblemDetails(response);
            this.handlePossibleAuthError(err);
        }

        return await response.text();
    }

    // Hand-written methods (incompatible with OpenApiClient signatures)

    updatePanelMigrationStatus(status: UpdatePanelMigrationStatusRequest): Promise<PanelMigrationStatus> {
        return this.JsonPut<PutSiteServicesV2PanelMigrationResult>('/panel_migration', status);
    }

    getPanelMigrationStatus(): Promise<PanelMigrationStatus> {
        return this.JsonGet<GetSiteServicesV2PanelMigrationResult>('/panel_migration');
    }

    async updateEmailSubscriptions(subscriptions: EmailSubscriptions): Promise<EmailSubscriptions> {
        return (await this.JsonPut<PutSiteServicesV2SubscriptionsResult>('/subscriptions', subscriptions))
            .email_subscriptions;
    }

    async createApiToken(name: string): Promise<ApiToken> {
        return await this.JsonPost<ApiToken>('/api-token/create', { name: name });
    }

    async updateApiToken(id: string, name: string): Promise<ApiToken> {
        return await this.JsonPost<ApiToken>(`/api-token/${encodeURIComponent(id)}/update`, { name: name });
    }

    async fetchPasswordInfo(passwordActionId: number): Promise<PasswordInfo> {
        return await this.JsonPost<PasswordInfo>(`/action-password-result/${passwordActionId}`, null);
    }

    async uploadBackup(request: UploadLocalImageRequest): Promise<PostSiteServicesV2UploadBackupResult> {
        return await this.JsonPost<PostSiteServicesV2UploadBackupResult>('/upload-backup', request);
    }

    async uploadBackupChunk(actionId: number, chunk: Blob): Promise<void> {
        const uploadTarget = this.endpoint + `/upload-backup/${encodeURIComponent(actionId)}`;
        const chunkResult = await fetch(uploadTarget, {
            credentials: 'include',
            cache: 'no-cache',
            method: 'POST',
            headers: {
                'Content-Type': 'application/octet-stream',
                'X-CSRF-TOKEN': this.csrfToken,
            },
            body: chunk,
        });

        if (!chunkResult.ok) {
            const err = await failedResponseAsProblemDetails(chunkResult);
            // eslint-disable-next-line @typescript-eslint/only-throw-error
            throw err;
        }
    }

    async getValidRegions(serverId: number, cancellationToken?: AbortSignal): Promise<RegionsResponseUnpaged> {
        const result = await this.JsonGet<GetSiteServicesV2ServersServerIdValidRegionsResult>(
            `/servers/${serverId}/valid_regions`,
            cancellationToken
        );
        return result;
    }

    async generateAuthenticatorSecret(): Promise<AuthenticatorCodeGeneration> {
        return await this.JsonGet<GetSiteServicesV2AuthenticatorGenerateNewSecretResult>(
            '/authenticator/generate-new-secret'
        );
    }

    async payInvoiceWithPaypal(
        invoiceNumber: number,
        completeUrl: string,
        actionId?: number
    ): Promise<PaypalDepositResult> {
        const baseUrl = window.location.origin;
        return await this.JsonPost<PaypalDepositResult>(`/invoice-payment/${invoiceNumber}/pay-with-paypal`, {
            transaction_success_url: completeUrl,
            paypal_success_return_url: `${baseUrl}/payment/paypal/process?invoiceid=${invoiceNumber}`,
            paypal_cancel_return_url: `${baseUrl}/payment/paypal/cancel?invoiceid=${invoiceNumber}`,
            blocked_action_id: actionId,
        });
    }

    async payDepositWithPaypal(amount: number, description: string, completeUrl: string): Promise<PaypalDepositResult> {
        const baseUrl = window.location.origin;
        return await this.JsonPost<PostSiteServicesV2AddCreditPaypalResult>(`/add-credit/paypal`, {
            transaction_success_url: completeUrl,
            paypal_success_return_url: `${baseUrl}/payment/paypal/process`,
            paypal_cancel_return_url: `${baseUrl}/payment/paypal/cancel`,
            amount_to_add: amount,
            description: description,
        });
    }

    async setupPaypal(successUrl: string): Promise<SetupPaypalResult> {
        const baseUrl = window.location.origin;
        return await this.JsonPost<SetupPaypalResult>('/billing-details/setup-paypal', {
            transaction_success_url: successUrl,
            paypal_success_return_url: `${baseUrl}/payment/paypal/process`,
            paypal_cancel_return_url: `${baseUrl}/payment/paypal/cancel`,
        });
    }

    async retryFailedInvoicePayments(): Promise<RetryInvoicePaymentsResult> {
        const result = await this.JsonPost<PostSiteServicesV2InvoicePaymentRetryFailedResult>(
            `/invoice-payment/retry-failed`,
            null
        );
        return result;
    }

    async getCardDetails(): Promise<CardDetailsResponse> {
        return await this.JsonGet<GetSiteServicesV2BillingDetailsCardDetailsResult>(`/billing-details/card-details`);
    }

    async updateDomainRegistrantDetails(domainName: string, request: ContactDetails): Promise<RegisteredDomain> {
        const result = await this.JsonPut<PutSiteServicesV2DomainManagementDomainNameUpdateContactDetailsResult>(
            `/domain-management/${encodeURIComponent(domainName)}/update-contact-details`,
            request
        );
        return result.registered_domain;
    }

    async getDomainTransferPlan(domainName: string, authCode: string): Promise<DomainTransferPlan> {
        const result = await this.JsonPost<PostSiteServicesV2DomainRegistrationDomainNameGetTransferPlanResult>(
            `/domain-registration/${encodeURIComponent(domainName)}/get-transfer-plan`,
            { auth_code: authCode }
        );
        return result.domain_transfer_plan;
    }

    async getDomainRegistrationPlans(domain: string): Promise<DomainRegistrationPlan[]> {
        const response = await this.JsonPost<PostSiteServicesV2DomainRegistrationGetRegistrationPlansResult>(
            `/domain-registration/get-registration-plans`,
            { domain: domain }
        );
        return response.domain_registration_plans;
    }

    async validateServerCreateParams(request: CreateServerRequest): Promise<void> {
        await this.JsonRequest<PostSiteServicesV2SignupValidateServerResult>(
            '/signup/validate-server',
            'POST',
            request
        );
        return;
    }
}
